<template>
  <div v-if="step !== 'hidden'" class="golden-ticket-classy" :class="{ 'gt-picker-mode': step !== 'ticket' }">
    <div v-if="step === 'ticket'" class="ticket-content">
      <div class="text-block">
        <p class="message">¡Felicidades!</p>
        <p class="subtitle">
          <span class="highlight">Golden Ticket</span>
        </p>
        <p class="amount">{{ goldenTamount }}<span class="currency">UND</span></p>
        <p class="custom-message">{{ goldenTmessage }}</p>
        <p v-if="errorMsg" class="gt-error gt-error-ticket" role="alert" aria-live="assertive">{{ errorMsg }}</p>
        <button class="gt-ticket-cta" :disabled="loadingTracks" @click="redeem">
          <span v-if="!loadingTracks">USAR</span>
          <span v-else class="waiting-text">Cargando...</span>
        </button>
      </div>
    </div>

    <div v-else-if="step === 'pickTrack'" class="gt-panel">
      <div class="gt-panel-header">
        <b>Elige un hipódromo</b>
        <button class="gt-close" aria-label="Cerrar" @click="cancelFlow">&times;</button>
      </div>
      <div v-if="loadingTracks" class="gt-loading">
        <span class="gt-spinner" aria-hidden="true"></span> Cargando hipódromos...
      </div>
      <div v-else-if="tracksError" class="gt-empty" role="alert" aria-live="assertive">
        <p>{{ tracksError }}</p>
        <button class="gt-retry" @click="loadTracks">Reintentar</button>
      </div>
      <div v-else-if="!tracks.length" class="gt-empty">
        <p>No hay hipódromos con actividad en este momento.</p>
        <button class="gt-retry" @click="loadTracks">Actualizar</button>
      </div>
      <ul v-else class="gt-list">
        <li
          v-for="tr in tracks"
          :key="tr.idhipodromo"
          class="gt-list-item"
          tabindex="0"
          @click="selectTrack(tr)"
          @keyup.enter="selectTrack(tr)"
        >
          <span class="gt-list-main">
            <b>{{ tr.nombre }}</b>
            <small>Carrera actual: {{ tr.idcarrera }}</small>
          </span>
          <span class="gt-badge" :class="statusClass(tr.estatus)">{{ statusLabel(tr.estatus) }}</span>
        </li>
      </ul>
      <div class="gt-actions">
        <button class="gt-back" @click="cancelFlow">Cancelar</button>
      </div>
    </div>

    <div v-else-if="step === 'pickRace'" class="gt-panel">
      <div class="gt-panel-header">
        <b>{{ selectedTrack.nombre }}</b>
        <button class="gt-close" aria-label="Cerrar" @click="cancelFlow">&times;</button>
      </div>
      <div v-if="loadingRaces" class="gt-loading">
        <span class="gt-spinner" aria-hidden="true"></span> Cargando carreras...
      </div>
      <div v-else-if="racesError" class="gt-empty" role="alert" aria-live="assertive">
        <p>{{ racesError }}</p>
        <button class="gt-retry" @click="loadRaces(selectedTrack)">Reintentar</button>
      </div>
      <div v-else-if="!races.length" class="gt-empty">
        <p>No hay carreras abiertas para este hipódromo.</p>
      </div>
      <ul v-else class="gt-list">
        <li
          v-for="r in races"
          :key="r.idcarrera"
          class="gt-list-item"
          tabindex="0"
          @click="selectRace(r)"
          @keyup.enter="selectRace(r)"
        >
          <span class="gt-list-main">
            <b>Carrera {{ r.idcarrera }}</b>
          </span>
          <span class="gt-badge gt-badge-open">ABIERTA</span>
        </li>
      </ul>
      <div class="gt-actions">
        <button class="gt-back" @click="back">Volver</button>
      </div>
    </div>

    <div v-else-if="step === 'pickHorse'" class="gt-panel">
      <div class="gt-panel-header">
        <b>{{ selectedTrack.nombre }} · Carrera {{ selectedRace.idcarrera }}</b>
        <button class="gt-close" aria-label="Cerrar" @click="cancelFlow">&times;</button>
      </div>
      <div v-if="loadingHorses" class="gt-loading">
        <span class="gt-spinner" aria-hidden="true"></span> Cargando ejemplares...
      </div>
      <div v-else-if="horsesError" class="gt-empty" role="alert" aria-live="assertive">
        <p>{{ horsesError }}</p>
        <button class="gt-retry" @click="loadHorses(selectedRace)">Reintentar</button>
      </div>
      <div v-else-if="!horses.length" class="gt-empty">
        <p>No hay ejemplares en esta carrera.</p>
      </div>
      <ul v-else class="gt-list">
        <li
          v-for="h in horses"
          :key="h.idejemplares"
          class="gt-list-item"
          :class="{ 'gt-list-item-disabled': h.estatus_ejemplar !== 'ACTIVO' }"
          :tabindex="h.estatus_ejemplar === 'ACTIVO' ? 0 : -1"
          @click="selectHorse(h)"
          @keyup.enter="selectHorse(h)"
        >
          <img
            class="gt-horse-number"
            :src="'https://trackandraces.com/dominio/ejemplares/' + h.numero"
            alt=""
          />
          <span class="gt-list-main">
            <b>{{ h.nombre }}</b>
            <small v-if="h.jinete">{{ h.jinete }}</small>
          </span>
          <span v-if="h.estatus_ejemplar !== 'ACTIVO'" class="gt-badge gt-badge-closed">RETIRADO</span>
        </li>
      </ul>
      <div class="gt-actions">
        <button class="gt-back" @click="back">Volver</button>
      </div>
    </div>

    <div v-else-if="step === 'confirm'" class="gt-panel">
      <div class="gt-panel-header">
        <b>Confirmar jugada</b>
        <button class="gt-close" aria-label="Cerrar" :disabled="loadingSubmit" @click="cancelFlow">&times;</button>
      </div>
      <div class="gt-summary">
        <div>{{ selectedTrack.nombre }} - Carrera {{ selectedRace.idcarrera }}</div>
        <div class="gt-summary-horse">
          Ganador:
          <img
            class="gt-horse-number"
            :src="'https://trackandraces.com/dominio/ejemplares/' + selectedHorse.numero"
            alt=""
          />
          <b>{{ selectedHorse.nombre }}</b>
        </div>
        <div>Monto: <b>{{ goldenTamount }} UND</b></div>
      </div>
      <p v-if="errorMsg" class="gt-error" role="alert" aria-live="assertive">{{ errorMsg }}</p>
      <div class="gt-actions">
        <button class="gt-back" :disabled="loadingSubmit" @click="back">Volver</button>
        <button class="gt-btn" :disabled="loadingSubmit" @click="confirmBet">
          <span v-if="!loadingSubmit">Confirmar</span>
          <span v-else class="waiting-text">Enviando...</span>
        </button>
      </div>
    </div>

    <div v-else-if="step === 'result'" class="gt-panel">
      <div class="gt-panel-header">
        <b>Resultado</b>
        <button class="gt-close" aria-label="Cerrar" @click="closeResult">&times;</button>
      </div>
      <div class="gt-result">
        <span
          class="gt-result-icon"
          :class="resultSuccess ? 'gt-icon-ok' : 'gt-icon-warn'"
          aria-hidden="true"
        >
          <svg v-if="resultSuccess" viewBox="0 0 24 24" width="26" height="26">
            <path fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="26" height="26">
            <path fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M12 8v5m0 3.5h.01M12 3l9 16H3z" />
          </svg>
        </span>
        <p role="alert" aria-live="assertive">{{ resultMsg }}</p>
        <p v-if="resultTicketId" class="gt-result-ticket">Ticket: <b>{{ resultTicketId }}</b></p>
      </div>
      <div class="gt-actions">
        <button class="gt-btn" @click="closeResult">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const API_BASE = "https://trackandraces.com/onlineapi/api/";
const SUCCESS_MSG = "La Apuesta se realizo con exito";

const api = axios.create({ baseURL: API_BASE });

function today() {
  const fechaDeAyer = () => {
    const hoy = new Date();
    const ayer = new Date(hoy.getTime() - 24 * 60 * 60 * 1000);
    const day = ayer.getDate();
    const month = ayer.getMonth() + 1;
    const year = ayer.getFullYear();
    return `${year}-${month < 10 ? "0" + month : month}-${day}`;
  };
  const date = new Date();
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hour = date.getHours();
  return hour < 4
    ? fechaDeAyer()
    : `${year}-${month < 10 ? "0" + month : month}-${day}`;
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default {
  name: "GoldenTicket",
  data() {
    return {
      step: "hidden", // hidden | ticket | pickTrack | pickRace | pickHorse | confirm | result

      goldenT: null,
      goldenTamount: null,
      goldenTmessage: "",
      userHasTicket: false,

      tracks: [],
      races: [],
      horses: [],
      selectedTrack: null,
      selectedRace: null,
      selectedHorse: null,
      idmoneda: null,

      loadingTracks: false,
      loadingRaces: false,
      loadingHorses: false,
      loadingSubmit: false,

      errorMsg: "",
      tracksError: "",
      racesError: "",
      horsesError: "",
      resultMsg: "",
      resultSuccess: false,
      resultTicketId: "",
    };
  },
  mounted() {
    this.getGoldenTicket();
  },
  methods: {
    hasToken() {
      return !!localStorage.getItem("token");
    },
    statusLabel(estatus) {
      if (estatus === "ABIERTA") return "ABIERTA";
      if (estatus === "CORRIENDO") return "EN VIVO";
      if (estatus === "CERRADA") return "CERRADA";
      return estatus;
    },
    statusClass(estatus) {
      if (estatus === "ABIERTA") return "gt-badge-open";
      if (estatus === "CORRIENDO") return "gt-badge-live";
      return "gt-badge-closed";
    },
    getGoldenTicket() {
      return api
        .get("/promo/goldenticket", { headers: authHeaders() })
        .then((response) => {
          if (response.data.goldenticket && response.data.goldenticket.length) {
            this.goldenT = response.data.goldenticket[0];
            this.goldenTamount = this.goldenT.monto;
            this.goldenTmessage = "Vence el " + this.goldenT.fecha_fin;
            this.userHasTicket = true;
          } else {
            this.userHasTicket = false;
          }
          this.step = this.userHasTicket ? "ticket" : "hidden";
        })
        .catch((error) => {
          console.error("Error al obtener el Golden Ticket:", error);
          this.userHasTicket = false;
          this.step = "hidden";
        });
    },
    redeem() {
      if (this.loadingTracks) return;
      this.errorMsg = "";
      if (!this.hasToken()) {
        this.errorMsg = "Debes iniciar sesión para usar el Golden Ticket.";
        return;
      }
      this.step = "pickTrack";
      this.loadTracks();
    },
    loadTracks() {
      this.loadingTracks = true;
      this.tracksError = "";
      api
        .get(`/mostrareventos_estatus/${today()}`, { headers: authHeaders() })
        .then((response) => {
          // Solo se oculta un hipódromo cuando su carrera actual está CERRADA;
          // ABIERTA o CORRIENDO se muestran igual, y la carrera concreta a
          // apostar (que debe estar ABIERTA) se elige en el siguiente paso.
          // Las ABIERTA se listan primero, luego las CORRIENDO.
          this.tracks = (response.data || [])
            .filter((e) => e.tipo === "HIPODROMO" && e.estatus !== "CERRADA")
            .sort((a, b) => (a.estatus === "ABIERTA" ? 0 : 1) - (b.estatus === "ABIERTA" ? 0 : 1));
        })
        .catch((error) => {
          console.error("Error al cargar hipódromos:", error);
          this.tracks = [];
          this.tracksError = "No se pudieron cargar los hipódromos.";
        })
        .finally(() => {
          this.loadingTracks = false;
        });
    },
    selectTrack(tr) {
      this.selectedTrack = tr;
      this.step = "pickRace";
      this.loadRaces(tr);
    },
    loadRaces(tr) {
      this.loadingRaces = true;
      this.racesError = "";
      api
        .get(`/cargarcarreras/${today()}/${tr.idhipodromo}`, { headers: authHeaders() })
        .then((response) => {
          this.races = (response.data || [])
            .filter((r) => r.estatus === "ABIERTA")
            .sort((a, b) => a.idcarrera - b.idcarrera);
        })
        .catch((error) => {
          console.error("Error al cargar carreras:", error);
          this.races = [];
          this.racesError = "No se pudieron cargar las carreras.";
        })
        .finally(() => {
          this.loadingRaces = false;
        });
    },
    selectRace(r) {
      // Solo se puede apostar mientras la carrera está ABIERTA: CORRIENDO ya
      // salió a pista y CERRADA ya finalizó.
      if (r.estatus !== "ABIERTA") return;
      this.selectedRace = r;
      this.step = "pickHorse";
      this.loadHorses(r);
    },
    loadHorses(r) {
      this.loadingHorses = true;
      this.horsesError = "";
      api
        .get(
          `/idcarrerachange_resultado/${r.idcarrera}/${today()}/${this.selectedTrack.idhipodromo}`,
          { headers: authHeaders() }
        )
        .then((response) => {
          // Se muestran todos los ejemplares (incluye RETIRADO) para que el
          // campo se vea completo; solo los ACTIVO son seleccionables.
          this.horses = response.data.ejemplares || [];
        })
        .catch((error) => {
          console.error("Error al cargar ejemplares:", error);
          this.horses = [];
          this.horsesError = "No se pudieron cargar los ejemplares.";
        })
        .finally(() => {
          this.loadingHorses = false;
        });
    },
    selectHorse(h) {
      if (h.estatus_ejemplar !== "ACTIVO") return;
      this.selectedHorse = h;
      this.errorMsg = "";
      this.step = "confirm";
    },
    loadIdMoneda() {
      return api.get("/new_saldo", { headers: authHeaders() }).then((response) => {
        this.idmoneda = response.data.saldo[0].idmoneda;
      });
    },
    async confirmBet() {
      if (this.loadingSubmit) return;
      if (!this.hasToken()) {
        this.errorMsg = "Debes iniciar sesión para usar el Golden Ticket.";
        return;
      }
      this.loadingSubmit = true;
      this.errorMsg = "";
      try {
        if (!this.idmoneda) {
          await this.loadIdMoneda();
        }
        const response = await api.post(
          "/golden_guardarticket",
          {
            idmoneda: this.idmoneda,
            bono: 0,
            idhipodromo: this.selectedTrack.idhipodromo,
            idcarrera: this.selectedRace.idcarrera,
            idjugadas: "Ganador",
            goldenticket: true,
            texto_jugada: String(this.selectedHorse.idejemplares),
            cantidad: this.goldenTamount,
            monto_jugada: this.goldenTamount,
          },
          { headers: authHeaders() }
        );
        this.resultMsg = response.data.mensaje || SUCCESS_MSG;
        this.resultSuccess = response.data.res === true;
        this.resultTicketId = response.data.idticket || "";
        this.step = "result";
        this.getGoldenTicket();
      } catch (error) {
        console.error("Error al confirmar la jugada del Golden Ticket:", error);
        this.errorMsg = "No se pudo procesar la jugada, intenta de nuevo.";
      } finally {
        this.loadingSubmit = false;
      }
    },
    back() {
      this.errorMsg = "";
      if (this.step === "confirm") {
        this.step = "pickHorse";
      } else if (this.step === "pickHorse") {
        this.selectedHorse = null;
        this.horses = [];
        this.step = "pickRace";
      } else if (this.step === "pickRace") {
        this.selectedRace = null;
        this.races = [];
        this.step = "pickTrack";
      } else {
        this.cancelFlow();
      }
    },
    cancelFlow() {
      if (this.loadingSubmit) return;
      this.selectedTrack = null;
      this.selectedRace = null;
      this.selectedHorse = null;
      this.tracks = [];
      this.races = [];
      this.horses = [];
      this.errorMsg = "";
      this.tracksError = "";
      this.racesError = "";
      this.horsesError = "";
      this.step = this.userHasTicket ? "ticket" : "hidden";
    },
    closeResult() {
      this.resultMsg = "";
      this.resultTicketId = "";
      this.selectedTrack = null;
      this.selectedRace = null;
      this.selectedHorse = null;
      this.step = this.userHasTicket ? "ticket" : "hidden";
    },
  },
};
</script>

<style scoped>
.golden-ticket-classy {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: 380px;
  max-width: 92vw;
  /* Proporción real de golden.png (643x325): con aspect-ratio fijo, el
     fondo "contain" siempre muestra el ticket completo, sin recortar
     el texto lateral ("GOLDEN TICKET" / "TRACK&RACES") ni el logo. */
  aspect-ratio: 643 / 325;
  background: url('https://trackandraces.com/dominio/tr/golden.png') center center / contain no-repeat;
  font-family: "Poppins", sans-serif;
  z-index: 9999;
  color: #212121;
  box-sizing: border-box;
  animation: bounceIn 0.6s ease-out, shakeLoop 3s infinite;
}

.golden-ticket-classy.gt-picker-mode {
  aspect-ratio: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  width: 320px;
  padding: 0;
  animation: bounceIn 0.3s ease-out;
}

.ticket-content {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  /* Deja libres las franjas laterales decorativas del ticket (texto
     vertical + logo) y centra el contenido en el medallón del medio. */
  padding: 6% 21% 6% 19%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.text-block {
  text-align: center;
  line-height: 1.1;
  width: 100%;
}

.message {
  font-size: 11px;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #212121;
}

.subtitle {
  font-size: 0;
  margin: 2px 0 0;
}

.highlight {
  font-size: 19px;
  font-weight: 900;
  color: #f9ed0a;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6), 0 0 4px #080808;
  letter-spacing: 0.5px;
}

.amount {
  font-size: 19px;
  font-weight: 800;
  color: #000;
  margin: 3px 0 0;
  letter-spacing: 0.5px;
}

.currency {
  font-size: 11px;
  font-weight: 600;
  color: #060606;
  margin-left: 2px;
}

.custom-message {
  font-size: 10px;
  font-style: italic;
  color: #060606;
  margin: 3px 0 6px;
  line-height: 1.3;
}

.waiting-text {
  color: #0d47a1 !important;
  font-weight: 600;
}

.gt-ticket-cta {
  min-height: 34px;
  min-width: 96px;
  border: none;
  border-radius: 6px;
  padding: 7px 22px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #1a1a1a;
  color: #fff;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 0.12s ease, opacity 0.15s ease;
}

.gt-ticket-cta:disabled {
  opacity: 0.6;
  cursor: default;
}

.gt-ticket-cta:active:not(:disabled) {
  transform: scale(0.97);
}

.gt-ticket-cta:focus-visible {
  outline: 2px solid #1565c0;
  outline-offset: 2px;
}

.gt-btn {
  margin-top: 3px;
  min-height: 44px;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  background: #212121;
  color: #f9ed0a;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 0.12s ease, opacity 0.15s ease;
}

.gt-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.gt-btn:active:not(:disabled) {
  transform: scale(0.97);
}

.gt-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 8px 8px 14px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #212121;
  text-align: left;
}

.gt-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #757575;
  padding: 0;
  width: 44px;
  height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  touch-action: manipulation;
  transition: background-color 0.15s ease;
}

.gt-close:hover {
  background: #f5f5f5;
}

.gt-close:disabled {
  opacity: 0.5;
  cursor: default;
}

.gt-loading,
.gt-empty {
  padding: 20px 16px;
  font-size: 13px;
  text-align: center;
  color: #616161;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.gt-loading {
  flex-direction: row;
  justify-content: center;
}

.gt-empty p {
  margin: 0;
}

.gt-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: #212121;
  border-radius: 50%;
  display: inline-block;
  animation: gtSpin 0.7s linear infinite;
}

.gt-retry {
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  color: #212121;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
}

.gt-retry:active {
  background: #f5f5f5;
}

.gt-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 260px;
  overflow-y: auto;
  text-align: left;
}

.gt-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 10px 14px;
  font-size: 14px;
  color: #212121;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  touch-action: manipulation;
  transition: background-color 0.15s ease;
}

.gt-list-item:hover {
  background: #fdf6d8;
}

.gt-list-item:active {
  background: #fbeba0;
}

.gt-list-item-disabled {
  cursor: default;
  opacity: 0.55;
}

.gt-list-item-disabled:hover,
.gt-list-item-disabled:active {
  background: transparent;
}

.gt-list-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.gt-list-main small {
  font-size: 11px;
  color: #757575;
  margin-top: 1px;
}

.gt-horse-number {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eee;
  object-fit: cover;
}

.gt-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  color: #fff;
  letter-spacing: 0.3px;
}

.gt-badge-open {
  background: #2e7d32;
}

.gt-badge-live {
  background: #e65100;
}

.gt-badge-closed {
  background: #757575;
}

.gt-summary {
  padding: 12px 14px;
  font-size: 13px;
  color: #212121;
  text-align: left;
  line-height: 1.7;
}

.gt-summary-horse {
  display: flex;
  align-items: center;
  gap: 6px;
}

.gt-result {
  padding: 20px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.gt-result p {
  margin: 0;
  font-size: 13px;
  color: #212121;
  line-height: 1.5;
}

.gt-result p.gt-result-ticket {
  font-size: 12px;
  color: #616161;
}

.gt-result-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gt-icon-ok {
  background: #e8f5e9;
  color: #2e7d32;
}

.gt-icon-warn {
  background: #fff3e0;
  color: #e65100;
}

.gt-error {
  margin: 0 14px 8px;
  font-size: 12px;
  color: #c62828;
}

.gt-error-ticket {
  margin: 6px 0 0;
  text-align: center;
}

.gt-actions {
  display: flex;
  gap: 8px;
  padding: 10px 14px 14px;
}

.gt-back {
  flex: 1;
  min-height: 44px;
  border: none;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #eee;
  color: #212121;
  touch-action: manipulation;
  transition: transform 0.12s ease, opacity 0.15s ease;
}

.gt-back:disabled {
  opacity: 0.6;
  cursor: default;
}

.gt-back:active:not(:disabled) {
  transform: scale(0.97);
}

.gt-actions .gt-btn {
  flex: 1;
  margin-top: 0;
}

.gt-list-item:focus-visible,
.gt-btn:focus-visible,
.gt-back:focus-visible,
.gt-retry:focus-visible,
.gt-close:focus-visible {
  outline: 2px solid #1565c0;
  outline-offset: 2px;
}

/* ANIMACIONES */
@keyframes gtSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .golden-ticket-classy {
    animation: none;
  }
  .golden-ticket-classy.gt-picker-mode {
    animation: none;
  }
}

@keyframes bounceIn {
  0% {
    transform: translateX(-50%) scale(0.7);
    opacity: 0;
  }
  60% {
    transform: translateX(-50%) scale(1.1);
    opacity: 1;
  }
  80% {
    transform: translateX(-50%) scale(0.95);
  }
  100% {
    transform: translateX(-50%) scale(1);
  }
}

@keyframes shakeLoop {
  0%,
  33.33%,
  100% {
    transform: translateX(-50%) translateY(0);
  }
  5% {
    transform: translateX(-50%) translateY(-2px);
  }
  10% {
    transform: translateX(-50%) translateY(2px);
  }
  15% {
    transform: translateX(-50%) translateY(-2px);
  }
  20% {
    transform: translateX(-50%) translateY(2px);
  }
  25% {
    transform: translateX(-50%) translateY(-2px);
  }
  30% {
    transform: translateX(-50%) translateY(0);
  }
}

/* RESPONSIVE */
@media (max-width: 600px) {
  .golden-ticket-classy {
    bottom: 70px;
    width: 90%;
    max-width: 300px;
  }

  .golden-ticket-classy.gt-picker-mode {
    width: 92vw;
    max-width: 340px;
  }

  .message {
    font-size: 10px;
  }

  .highlight {
    font-size: 15px;
  }

  .amount {
    font-size: 16px;
  }

  .custom-message {
    font-size: 9px;
  }

  .gt-ticket-cta {
    min-height: 30px;
    min-width: 80px;
    padding: 6px 16px;
    font-size: 11px;
  }
}
</style>
